import {QueryClient, UseQueryResult, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import IWBrokerClient from '../client/IWBrokerClient';
import {gatewayRealtimeQueryOptions} from './gatewayQueryUtils';

export type HVACGroup = Record<string, any> & {id: number};
export type HVACGroups = Record<number, HVACGroup>;

export const hvacGroupsQueryKey = ['hvac group list'] as const;

const installedHVACGroupsListeners = new WeakSet<QueryClient>();

function installHVACGroupsListener(queryClient: QueryClient) {
    if (installedHVACGroupsListeners.has(queryClient)) {
        return;
    }

    IWBrokerClient.socket.on('hvac group update', (group: HVACGroup) => {
        console.log('(hvacGroupsQuery) hvac group update', group);
        queryClient.setQueryData<HVACGroups>(hvacGroupsQueryKey, (current: HVACGroups | undefined) => ({
            ...current,
            [group.id]: group
        }));
    });

    installedHVACGroupsListeners.add(queryClient);
}

export function useHVACGroupsQuery(): UseQueryResult<HVACGroups, Error> {
    const queryClient = useQueryClient();
    installHVACGroupsListener(queryClient);

    return useQuery<HVACGroups>({
        queryKey: hvacGroupsQueryKey,
        queryFn: async () => IWBrokerClient.request('hvac group list') as Promise<HVACGroups>,
        ...gatewayRealtimeQueryOptions
    });
}

interface SetHVACGroupSettingVariables {
    groupId: number;
    setting: string;
    value: any;
}

export function useSetHVACGroupSettingMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['hvac group setting'],
        mutationFn: async ({groupId, setting, value}: SetHVACGroupSettingVariables) => {
            await IWBrokerClient.request('hvac group setting', {groupId, setting, value});
        },
        onMutate: async ({groupId, setting, value}) => {
            await queryClient.cancelQueries({queryKey: hvacGroupsQueryKey});
            const previousGroups = queryClient.getQueryData<HVACGroups>(hvacGroupsQueryKey);

            queryClient.setQueryData<HVACGroups>(hvacGroupsQueryKey, (current: HVACGroups | undefined) => {
                if (!current?.[groupId]) {
                    return current;
                }

                return {
                    ...current,
                    [groupId]: {
                        ...current[groupId],
                        [setting]: value
                    }
                };
            });

            return {previousGroups};
        },
        onError: (_error, _variables, context) => {
            if (context?.previousGroups) {
                queryClient.setQueryData(hvacGroupsQueryKey, context.previousGroups);
            }
        }
    });
}
